import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS } from "@/data/siteData";

export default function Products() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const categories = useMemo(() => ["all", ...new Set(PRODUCTS.map((p) => p.category))], []);

  const filtered = useMemo(() => PRODUCTS.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.short_description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === "all" || p.category === category;
    return matchesSearch && matchesCategory;
  }), [search, category]);

  return (
    <div className="grid-bg min-h-screen">
      {/* Header */}
      <div
        className="relative py-20"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid rgba(0,240,255,0.08)' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <span
            className="text-[11px] tracking-[0.2em] uppercase block mb-2"
            style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}
          >
            Research Catalog
          </span>
          <h1
            className="text-4xl sm:text-5xl font-bold tracking-tighter mb-4"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            All Compounds
          </h1>
          <p className="text-sm max-w-2xl" style={{ color: 'var(--text-secondary)' }}>
            Browse our complete catalog of research-grade peptides and compounds. All products are supplied as lyophilized powder with &gt;99% verified purity.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
            <Input
              data-testid="product-search-input"
              placeholder="Search compounds..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 text-sm"
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid rgba(0,240,255,0.15)',
                color: 'var(--text-primary)',
                fontFamily: "'Roboto Mono', monospace",
              }}
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              data-testid="category-filter-select"
              className="w-full sm:w-52 h-10 text-sm"
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid rgba(0,240,255,0.15)',
                color: 'var(--text-primary)',
                fontFamily: "'Roboto Mono', monospace",
              }}
            >
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid rgba(0,240,255,0.2)',
              }}
            >
              {categories.map((cat) => (
                <SelectItem
                  key={cat}
                  value={cat}
                  className="text-sm cursor-pointer"
                  style={{ fontFamily: "'Roboto Mono', monospace" }}
                >
                  {cat === "all" ? "All Categories" : cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>
          Showing {filtered.length} of {PRODUCTS.length} compounds
        </p>

        {/* Product Grid */}
        <div data-testid="products-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-16">
          {filtered.map((product, i) => (
            <ProductCard key={product.slug} product={product} index={i} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-lg" style={{ fontFamily: "'Rajdhani', sans-serif", color: 'var(--text-secondary)' }}>
              No compounds match your search criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
